import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, input, NgZone, OnDestroy, signal } from '@angular/core';

type MascotMood = 'idle' | 'happy' | 'excited';

const MOOD_EMOJI: Record<MascotMood, string> = {
  idle: '👀',
  happy: '😋',
  excited: '🍔',
};


@Component({
  selector: 'lib-ui-modal-mascot',
  templateUrl: './ui-modal-mascot.component.html',
  styleUrl: './ui-modal-mascot.component.scss',
   changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiModalMascotComponent  implements AfterViewInit, OnDestroy {
  readonly src = input.required<string>();
  readonly alt = input<string>('');

  protected readonly mood = signal<MascotMood>('idle');
  protected readonly blinking = signal<boolean>(false);
  protected readonly bubbleVisible = signal<boolean>(false);
  protected readonly bubbleEmoji = signal<string>(MOOD_EMOJI.idle);

  private pointerX = 0;
  private pointerY = 0;

  private frameId: number | null = null;
  private blinkTimerId: number | null = null;
  private blinkReleaseTimerId: number | null = null;
  private moodTimerId: number | null = null;
  private removePointerMoveListener: (() => void) | null = null;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly ngZone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.pointerX = window.innerWidth / 2;
    this.pointerY = window.innerHeight / 2;

    this.ngZone.runOutsideAngular(() => {
      const onPointerMove = (event: PointerEvent): void => {
        this.pointerX = event.clientX;
        this.pointerY = event.clientY;
      };

      window.addEventListener('pointermove', onPointerMove, { passive: true });

      this.removePointerMoveListener = () => {
        window.removeEventListener('pointermove', onPointerMove);
      };

      this.startEyeLoop();
      this.scheduleBlink();
    });
  }

  ngOnDestroy(): void {
    this.removePointerMoveListener?.();

    if (typeof window === 'undefined') {
      return;
    }

    if (this.frameId !== null) {
      window.cancelAnimationFrame(this.frameId);
    }

    if (this.blinkTimerId !== null) {
      window.clearTimeout(this.blinkTimerId);
    }

    if (this.blinkReleaseTimerId !== null) {
      window.clearTimeout(this.blinkReleaseTimerId);
    }

    if (this.moodTimerId !== null) {
      window.clearTimeout(this.moodTimerId);
    }
  }

  react(nextMood: MascotMood): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.ngZone.run(() => {
      this.mood.set(nextMood);
      this.bubbleEmoji.set(MOOD_EMOJI[nextMood]);
      this.bubbleVisible.set(true);
    });

    if (this.moodTimerId !== null) {
      window.clearTimeout(this.moodTimerId);
    }

    this.moodTimerId = window.setTimeout(() => {
      this.ngZone.run(() => {
        this.bubbleVisible.set(false);
        this.mood.set('idle');
      });
    }, 1400);
  }

  private startEyeLoop(): void {
    const render = (): void => {
      this.updateEyes();
      this.frameId = window.requestAnimationFrame(render);
    };

    render();
  }

  private updateEyes(): void {
    const eyes =
      this.elementRef.nativeElement.querySelectorAll<HTMLElement>('[data-eye]');

    eyes.forEach((eye) => {
      const pupil = eye.querySelector<HTMLElement>('[data-pupil]');

      if (!pupil) {
        return;
      }

      const rect = eye.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const dx = this.pointerX - centerX;
      const dy = this.pointerY - centerY;

      const angle = Math.atan2(dy, dx);
      const distance = Math.min(Math.hypot(dx, dy) / 18, 1);

      const maxMoveX = 7;
      const maxMoveY = 8;

      const x = Math.cos(angle) * maxMoveX * distance;
      const y = Math.sin(angle) * maxMoveY * distance;

      pupil.style.setProperty('--pupil-x', `${x.toFixed(2)}px`);
      pupil.style.setProperty('--pupil-y', `${y.toFixed(2)}px`);
    });
  }

  private scheduleBlink(): void {
    const nextBlinkDelay = 2200 + Math.random() * 3200;

    this.blinkTimerId = window.setTimeout(() => {
      this.ngZone.run(() => {
        this.blinking.set(true);
      });

      this.blinkReleaseTimerId = window.setTimeout(() => {
        this.ngZone.run(() => {
          this.blinking.set(false);
        });

        this.scheduleBlink();
      }, 170);
    }, nextBlinkDelay);
  }
}